import { XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import React, { useId, useState } from 'react';
import { inputClassName } from '../../../../atoms/Surface';
import { splitEmails } from './taskDraft';

/**
 * A list of e-mails as chips: typed one by one (Enter, comma or leaving the field adds it) or pasted many at
 * once from a sheet or a mail. `suggestions` are the people already on tasks.
 */
export function EmailsInput({
  label,
  value,
  onChange,
  suggestions = [],
  error,
  hint,
}: {
  label: string;
  value: string[];
  onChange: (emails: string[]) => void;
  suggestions?: string[];
  error?: string;
  hint?: string;
}) {
  const [text, setText] = useState('');
  const listId = useId();
  const add = (raw: string) => {
    const added = splitEmails(raw).filter(email => !value.includes(email));
    if (added.length) {
      onChange([...value, ...added]);
    }
    setText('');
  };
  return (
    <div className="flex flex-col gap-1">
      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-dl-muted">
          {label}{' '}
          <span className="font-normal text-dl-faint">({value.length})</span>
        </span>
        <input
          className={classNames(inputClassName, {
            'ring-dl-danger': !!error,
          })}
          list={listId}
          value={text}
          placeholder="name@gleamer.ai — paste several at once"
          onChange={e =>
            /[\s,;]$/.test(e.target.value)
              ? add(e.target.value)
              : setText(e.target.value)
          }
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(text);
            }
          }}
          onPaste={e => {
            e.preventDefault();
            add(text + ' ' + e.clipboardData.getData('text'));
          }}
          onBlur={() => text && add(text)}
        />
      </label>
      <datalist id={listId}>
        {suggestions
          .filter(s => !value.includes(s))
          .map(s => (
            <option key={s} value={s} />
          ))}
      </datalist>
      {value.length > 0 && (
        <ul
          className="flex flex-wrap gap-1 pt-1"
          aria-label={`${label} chosen`}
        >
          {value.map(email => (
            <li
              key={email}
              className="flex items-center gap-1 rounded-full bg-dl-tint py-0.5 pl-3 pr-1 text-sm text-dl-accent"
            >
              {email}
              <button
                type="button"
                aria-label={`Remove ${email}`}
                onClick={() => onChange(value.filter(e => e !== email))}
                className="rounded-full p-0.5 hover:bg-dl-surface"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {hint && !error && <span className="text-sm text-dl-faint">{hint}</span>}
      {error && <span className="text-sm text-dl-danger">{error}</span>}
    </div>
  );
}
